import { PropsWithChildren, useContext, useEffect } from "react";
import { AuthContext, AuthProvider, IAuthContext, TAuthConfig } from "react-oauth2-code-pkce";

import { useFrontendSetting } from "../../lib/hooks/use-frontend-settings";
import {
  REFRESH_TOKEN_EXPIRY_TIME,
  SESSION_KEY_RECENT_IDENTITY_PROVIDER,
  SESSION_KEY_RECENT_PATH,
  SESSION_KEY_RECENT_TENANT,
  TOKEN_EXPIRY_TIME,
  getLoginRedirect,
} from "../auth-constants";

import { chosenIdentityProviderKey } from "./tenant-provider";

interface AuthProviderForIdentityProviderProps extends PropsWithChildren {
  tenant: string;
  identityProvider: string;
  authConfig?: Partial<TAuthConfig>;
  oauthRedirectUrlPrefix?: string;
}

export function AuthProviderForIdentityProvider({
  tenant,
  identityProvider,
  authConfig = {},
  oauthRedirectUrlPrefix,
  children,
}: AuthProviderForIdentityProviderProps) {
  const { authorizationUrl, nativeClientId, defaultClientId, tokenUrl, scope, isSuccess } =
    useFrontendSetting();

  if (!isSuccess) return null;

  const actualAuthConfig: TAuthConfig = {
    clientId: identityProvider === "rana" ? nativeClientId : defaultClientId,
    authorizationEndpoint: authorizationUrl,
    tokenEndpoint: tokenUrl,
    redirectUri: getLoginRedirect(oauthRedirectUrlPrefix),
    scope: `openid profile email ${scope}`,
    tokenExpiresIn: TOKEN_EXPIRY_TIME,
    refreshTokenExpiresIn: REFRESH_TOKEN_EXPIRY_TIME,
    extraAuthParameters: {
      identity_provider: identityProvider === "rana" ? "" : identityProvider,
    },
    autoLogin: true,
    preLogin: () => {
      // Set these in the session so that the callback page can find them
      window.sessionStorage.setItem(SESSION_KEY_RECENT_TENANT, tenant);
      window.sessionStorage.setItem(SESSION_KEY_RECENT_IDENTITY_PROVIDER, identityProvider);
      window.sessionStorage.setItem(
        SESSION_KEY_RECENT_PATH,
        window.location.pathname + window.location.search
      );
    },

    // Is saved on session state and validated by react-oauth2-code-pkce
    state: crypto.randomUUID(),

    storage: "session",

    // Store auth information independently per tenant and identity provider combination
    storageKeyPrefix: `RANA_AUTH_${tenant}_${identityProvider}`,

    ...authConfig,
  };

  return (
    <AuthProvider authConfig={actualAuthConfig}>
      <SyncIdentityProvider tenant={tenant} identityProvider={identityProvider} />

      {children}
    </AuthProvider>
  );
}

// When the user is logged in we want to make sure the identity provider in the local storage matches up with the chosen one
// We used to do this in the cognito callback page but that did not take into account the local storage being emptied but the user still having a valid session
const SyncIdentityProvider = ({
  identityProvider,
  tenant,
}: {
  identityProvider: string;
  tenant: string;
}) => {
  const authContext = useContext<IAuthContext>(AuthContext);
  useEffect(() => {
    const loggedIn = !!(authContext.token && authContext.idToken);

    if (!loggedIn) return;

    const key = chosenIdentityProviderKey(tenant);
    const storageIdenityProvider = window.localStorage.getItem(key);

    if (storageIdenityProvider !== identityProvider)
      window.localStorage.setItem(key, identityProvider);
  }, [identityProvider, tenant, authContext]);

  return null;
};
